import type { Graphics } from 'pixi.js';
import type { WeaponSlotRenderInfo } from '../labyrinth/Robot';

// ─── Solid colors (midpoints of former gradients) ─────────────
const TIRE_COLOR = 0x22262e;
const AXLE_COLOR = 0x6a7888;
const BOLT_COLOR = 0x808898;
const BODY_COLOR = 0xb8bec8;
const RING_COLOR = 0x8a90a0;
const DOME_COLOR = 0xa8aec0;
const BARREL_COLOR = 0x4a5060;
const MUZZLE_COLOR = 0x141a28;
const BARREL_MOUNT_COLOR = 0x2d323f;
const BARREL_RING_COLOR = 0x4a5060;

// ─── Weapon category colors ──────────────────────────────────
const WEAPON_COLORS = {
  ballistic: 0xddaa44,
  energy:    0x44ccff,
  rocket:    0xff8833,
  bomb:      0xff4400,
  melee:     0xffaa00,
} as const;

// ─── Weapon slot offset layouts (fractions of scale) ──────────
// Each slot has { x: forward/back offset, y: lateral offset }
export interface SlotOffset { x: number; y: number }
export const SLOT_OFFSETS: SlotOffset[][] = [
  [{ x: 0, y: 0 }],                                                          // 1 slot: center
  [{ x: 0, y: -0.18 }, { x: 0, y: 0.18 }],                                  // 2 slots: symmetric pair
  [{ x: 0, y: 0 }, { x: 0, y: -0.26 }, { x: 0, y: 0.26 }],                 // 3 slots: center + flankers
  [{ x: 0, y: -0.13 }, { x: 0, y: 0.13 }, { x: -0.18, y: -0.36 }, { x: -0.18, y: 0.36 }],  // 4 slots: inner + outer pairs
];

// ─── Helpers ───────────────────────────────────────────────────

/** Draw a bolt/rivet centered at (bx,by) with radius r */
function drawBolt(g: Graphics, bx: number, by: number, r: number) {
  g.circle(bx, by, r);
  g.fill(BOLT_COLOR);
  g.stroke({ color: 0x303440, width: 0.4 });
  // specular dot
  g.circle(bx - r * 0.28, by - r * 0.28, r * 0.28);
  g.fill({ color: 0xffffff, alpha: 0.55 });
}

// ─── Body ──────────────────────────────────────────────────────

/** Draw the drop shadow only — meant for a separate blurred Graphics layer. */
export function drawRobotShadow(g: Graphics, s: number) {
  // ¾-view: shadow shifted south (toward viewer)
  g.ellipse(s * 0.05, s * 0.18, s * 0.85, s * 0.75);
  g.fill({ color: 0x000000, alpha: 0.5 });
}

export function drawRobotBody(
  g: Graphics,
  s: number,
  t: number = 0,
  walkPhase: number = 0,
  moving: boolean = false,
  turnRate: number = 0,
) {
  // ── Wheels (6 wheels: 3 per side) ────────────────────────────
  // Top-down view: rounded rectangles with horizontal tread grooves
  const wheelLen = s * 0.45;       // length along chassis axis (front-to-back)
  const wheelWid = s * 0.27;       // width across chassis axis (side-to-side)
  const wheelRound = s * 0.09;     // corner radius
  const treadCount = 5;            // number of tread grooves
  const treadGap = wheelLen / (treadCount + 1); // spacing between grooves

  // Clamp turn rate for animation (-1 to 1 range)
  const turn = Math.max(-1, Math.min(1, turnRate * 0.6));

  // Wheel positions: [x, y] — 3 rows, 2 sides
  const wheels: [number, number][] = [
    [ s * 0.48, -s * 0.50],   // front-left
    [ s * 0.48,  s * 0.50],   // front-right
    [ 0,        -s * 0.50],   // mid-left
    [ 0,         s * 0.50],   // mid-right
    [-s * 0.48, -s * 0.50],   // rear-left
    [-s * 0.48,  s * 0.50],   // rear-right
  ];

  for (const [wx, wy] of wheels) {
    const side = wy < 0 ? -1 : 1; // -1 = left, 1 = right

    // Differential wheel spin: outer wheels spin faster when turning
    const turnSpin = -side * turn * 1.2;
    const spinOffset = walkPhase + turnSpin;

    // Wheel center (offset outward from chassis edge)
    const wcY = wy + side * wheelWid * 0.5;

    // ── Axle mount bracket (connects wheel to chassis) ──
    const bracketW = s * 0.10;
    const bracketLen = wheelWid * 0.6;
    g.roundRect(wx - bracketW / 2, Math.min(wy, wcY), bracketW, bracketLen, s * 0.02);
    g.fill(AXLE_COLOR);
    g.stroke({ color: 0x1a1e26, width: 0.8 });

    // ── Wheel shadow ──
    g.roundRect(wx - wheelLen / 2 + 1.5, wcY - wheelWid / 2 + 1.5,
                wheelLen, wheelWid, wheelRound);
    g.fill({ color: 0x000000, alpha: 0.3 });

    // ── Tire body (rounded rectangle) ──
    g.roundRect(wx - wheelLen / 2, wcY - wheelWid / 2,
                wheelLen, wheelWid, wheelRound);
    g.fill(TIRE_COLOR);
    g.stroke({ color: 0x0e1218, width: 1.4 });

    // ── Tread grooves (horizontal lines that scroll with movement) ──
    // Use modulo to scroll treads along the wheel length
    const scrollOffset = ((spinOffset * wheelLen / (Math.PI * 2)) % treadGap + treadGap) % treadGap;
    const treadInset = wheelWid * 0.12; // inset from tire edge

    for (let i = -1; i <= treadCount + 1; i++) {
      const tx = wx - wheelLen / 2 + scrollOffset + i * treadGap;
      // Clip to wheel bounds
      if (tx > wx - wheelLen / 2 + wheelRound * 0.5 &&
          tx < wx + wheelLen / 2 - wheelRound * 0.5) {
        g.moveTo(tx, wcY - wheelWid / 2 + treadInset);
        g.lineTo(tx, wcY + wheelWid / 2 - treadInset);
        g.stroke({ color: 0x3a4048, width: 2.0 });
      }
    }

    // ── Sidewall edges (subtle inner border) ──
    g.roundRect(wx - wheelLen / 2 + 2, wcY - wheelWid / 2 + 1.5,
                wheelLen - 4, wheelWid - 3, wheelRound * 0.7);
    g.stroke({ color: 0x2a2e36, width: 0.8 });

    // ── Top highlight (subtle rubber sheen) ──
    g.roundRect(wx - wheelLen / 2 + 3, wcY - wheelWid / 2 + 2,
                wheelLen - 6, wheelWid * 0.3, wheelRound * 0.5);
    g.fill({ color: 0xffffff, alpha: 0.04 });
  }

  // ── Main chassis body ───────────────────────────────────────
  // Octagonal hull: wider through the middle (side panels), narrower
  // at front/rear, with diagonal corners connecting them.
  // Hourglass inner panel pinches inward at the center.
  const bodyPts = [
    [ 0.55, -0.32],   // front-left
    [ 0.44, -0.50],   // front-left diagonal → side panel
    [-0.44, -0.50],   // left side panel (long edge)
    [-0.55, -0.32],   // rear-left diagonal ← side panel
    [-0.55,  0.32],   // rear-right
    [-0.44,  0.50],   // rear-right diagonal → side panel
    [ 0.44,  0.50],   // right side panel (long edge)
    [ 0.55,  0.32],   // front-right diagonal ← side panel
  ] as const;

  // Shadow/thickness face (¾-view: offset straight down toward viewer)
  const sh = 3;
  g.moveTo(s * bodyPts[0][0], s * bodyPts[0][1] + sh);
  for (let i = 1; i < bodyPts.length; i++) {
    g.lineTo(s * bodyPts[i]![0], s * bodyPts[i]![1] + sh);
  }
  g.closePath();
  g.fill({ color: 0x000000, alpha: 0.35 });

  // Top face
  g.moveTo(s * bodyPts[0][0], s * bodyPts[0][1]);
  for (let i = 1; i < bodyPts.length; i++) {
    g.lineTo(s * bodyPts[i]![0], s * bodyPts[i]![1]);
  }
  g.closePath();
  g.fill(BODY_COLOR);
  g.stroke({ color: 0x6a7080, width: 1.4 });

  // Edge bevel — bright highlight on front and upper-left edges
  g.moveTo(s * 0.55, -s * 0.32);
  g.lineTo(s * 0.55, s * 0.32);
  g.stroke({ color: 0xffffff, alpha: 0.18, width: 1.5 });
  g.moveTo(s * 0.55, -s * 0.32);
  g.lineTo(s * 0.44, -s * 0.50);
  g.lineTo(-s * 0.44, -s * 0.50);
  g.stroke({ color: 0xffffff, alpha: 0.14, width: 1.5 });

  // Edge bevel — shadow on rear and lower-right edges (¾-view: stronger south-facing shadow)
  g.moveTo(-s * 0.55, s * 0.32);
  g.lineTo(-s * 0.55, -s * 0.32);
  g.stroke({ color: 0x000000, alpha: 0.30, width: 1.5 });
  g.moveTo(-s * 0.55, s * 0.32);
  g.lineTo(-s * 0.44, s * 0.50);
  g.lineTo(s * 0.44, s * 0.50);
  g.stroke({ color: 0x000000, alpha: 0.28, width: 1.5 });

  // ── Side armor panels (darker overlay on side edges) ────────
  for (const side of [-1, 1]) {
    // Trapezoidal side panel area (between diagonal corners)
    g.moveTo(s * 0.44, side * s * 0.50);
    g.lineTo(s * 0.55, side * s * 0.32);
    g.lineTo(s * 0.55, side * s * 0.28);
    g.lineTo(s * 0.44, side * s * 0.44);
    g.lineTo(-s * 0.44, side * s * 0.44);
    g.lineTo(-s * 0.55, side * s * 0.28);
    g.lineTo(-s * 0.55, side * s * 0.32);
    g.lineTo(-s * 0.44, side * s * 0.50);
    g.closePath();
    g.fill({ color: 0x000000, alpha: 0.08 });
    // Inner seam line (where center panel meets side panel)
    g.moveTo(s * 0.44, side * s * 0.44);
    g.lineTo(-s * 0.44, side * s * 0.44);
    g.stroke({ color: 0x555a68, width: 1.0 });
    g.moveTo(s * 0.44, side * s * 0.434);
    g.lineTo(-s * 0.44, side * s * 0.434);
    g.stroke({ color: 0xe0e4f0, alpha: 0.3, width: 0.5 });
  }

  // ── Center panel hourglass contour (filled) ─────────────────
  // Lighter inner panel that narrows at the center
  g.moveTo(s * 0.44, -s * 0.36);
  g.lineTo(s * 0.0, -s * 0.20);
  g.lineTo(-s * 0.44, -s * 0.36);
  g.lineTo(-s * 0.44, s * 0.36);
  g.lineTo(s * 0.0, s * 0.20);
  g.lineTo(s * 0.44, s * 0.36);
  g.closePath();
  g.fill({ color: 0xffffff, alpha: 0.06 });
  // Hourglass contour stroke lines
  for (const side of [-1, 1]) {
    g.moveTo(s * 0.44, side * s * 0.36);
    g.lineTo(s * 0.0, side * s * 0.20);
    g.lineTo(-s * 0.44, side * s * 0.36);
    g.stroke({ color: 0x555a68, width: 1.2 });
    // Highlight parallel
    g.moveTo(s * 0.44, side * s * 0.352);
    g.lineTo(s * 0.0, side * s * 0.192);
    g.lineTo(-s * 0.44, side * s * 0.352);
    g.stroke({ color: 0xe0e8f8, alpha: 0.35, width: 0.5 });
  }

  // ── Panel detail lines ──────────────────────────────────────
  // Front cross panel
  g.moveTo(s * 0.35, -s * 0.44);
  g.lineTo(s * 0.35, s * 0.44);
  g.stroke({ color: 0x6a7080, width: 0.8 });
  // Rear cross panel
  g.moveTo(-s * 0.35, -s * 0.44);
  g.lineTo(-s * 0.35, s * 0.44);
  g.stroke({ color: 0x6a7080, width: 0.8 });

  // ── Panel rivets ─────────────────────────────────────────────
  const rivetR = s * 0.028;
  for (const [rx, ry] of [
    [ 0.48, -0.24], [ 0.48,  0.24],
    [-0.48, -0.24], [-0.48,  0.24],
    [ 0.0,  -0.47], [ 0.0,   0.47],
    [ 0.30, -0.46], [ 0.30,  0.46],
    [-0.30, -0.46], [-0.30,  0.46],
  ] as [number, number][]) {
    drawBolt(g, s * rx, s * ry, rivetR);
  }

  // ── Turret ring ──────────────────────────────────────────────
  g.circle(s * 0.02, s * 0.02, s * 0.39);
  g.fill({ color: 0x000000, alpha: 0.3 });
  g.circle(0, 0, s * 0.385);
  g.fill(RING_COLOR);
  g.stroke({ color: 0x50576a, width: 1.2 });
  g.circle(0, 0, s * 0.34);
  g.stroke({ color: 0x9098a8, alpha: 0.5, width: 0.5 });

  // ── Front lights (white rectangular) ─────────────────────────
  const hlf = 0.72 + Math.sin(t * 6) * 0.15;
  for (const sy of [-0.24, 0.24]) {
    // Housing recess
    g.rect(s * 0.49, s * sy - s * 0.045, s * 0.055, s * 0.09);
    g.fill({ color: 0x000000, alpha: 0.5 });
    // Outer glow
    g.rect(s * 0.43, s * sy - s * 0.10, s * 0.18, s * 0.20);
    g.fill({ color: 0xeef6ff, alpha: hlf * 0.06 });
    // Bright lens
    g.rect(s * 0.495, s * sy - s * 0.038, s * 0.045, s * 0.076);
    g.fill({ color: 0xffffff, alpha: hlf * 0.95 });
    // Specular
    g.rect(s * 0.50, s * sy - s * 0.030, s * 0.018, s * 0.022);
    g.fill({ color: 0xffffff, alpha: hlf });
  }

  // ── Rear lights (red rectangular) ────────────────────────────
  const rearPulse = 0.55 + Math.sin(t * 2) * 0.3;
  for (const sy of [-0.24, 0.24]) {
    // Housing recess
    g.rect(-s * 0.545, s * sy - s * 0.045, s * 0.055, s * 0.09);
    g.fill({ color: 0x000000, alpha: 0.5 });
    // Red glow
    g.rect(-s * 0.58, s * sy - s * 0.10, s * 0.18, s * 0.20);
    g.fill({ color: 0xff2010, alpha: rearPulse * 0.08 });
    // Bright lens
    g.rect(-s * 0.54, s * sy - s * 0.038, s * 0.045, s * 0.076);
    g.fill({ color: 0xff4030, alpha: rearPulse * 0.9 });
    // Specular
    g.rect(-s * 0.535, s * sy - s * 0.030, s * 0.018, s * 0.022);
    g.fill({ color: 0xffffff, alpha: rearPulse * 0.5 });
  }

  // ── Rear exhaust vents ───────────────────────────────────────
  for (const vy of [-0.10, 0, 0.10]) {
    g.rect(-s * 0.545, s * vy - s * 0.025, s * 0.09, s * 0.050);
    g.fill({ color: 0x000000, alpha: 0.6 });
    g.rect(-s * 0.535, s * vy - s * 0.020, s * 0.07, s * 0.040);
    g.fill(0x0c1018);
    for (const lx of [0.50, 0.515, 0.528]) {
      g.moveTo(-s * lx, s * vy - s * 0.016);
      g.lineTo(-s * lx, s * vy + s * 0.016);
      g.stroke({ color: 0x1e2530, width: 0.6 });
    }
  }

  // ── Exhaust glow ─────────────────────────────────────────────
  if (moving) {
    const eg = 0.3 + Math.sin(t * 8) * 0.15 + Math.sin(t * 23) * 0.08;
    g.circle(-s * 0.62, 0, s * 0.22);
    g.fill({ color: 0x1144cc, alpha: eg * 0.12 });
    g.circle(-s * 0.62, 0, s * 0.14);
    g.fill({ color: 0x3366ff, alpha: eg * 0.32 });
    g.circle(-s * 0.62, 0, s * 0.06);
    g.fill({ color: 0x88aaff, alpha: eg * 0.65 });
    g.circle(-s * 0.62, 0, s * 0.02);
    g.fill({ color: 0xffffff, alpha: eg * 0.8 });
  }

  // ── Side running lights ───────────────────────────────────────
  const sP1 = 0.5 + Math.sin(t * 4.5) * 0.5;
  const sP2 = 0.5 + Math.sin(t * 4.5 + Math.PI) * 0.5;
  for (const [x, y, ph] of [
    [ 0.15, -0.49,  sP1],
    [-0.15, -0.49,  sP2],
    [ 0.15,  0.49,  sP2],
    [-0.15,  0.49,  sP1],
  ] as [number, number, number][]) {
    g.circle(s * x, s * y, s * 0.06);
    g.fill({ color: 0xffaa20, alpha: ph * 0.18 });
    g.circle(s * x, s * y, s * 0.035);
    g.fill({ color: 0xffcc40, alpha: ph * 0.92 });
    g.circle(s * x - s * 0.012, s * y - s * 0.012, s * 0.013);
    g.fill({ color: 0xffffff, alpha: ph * 0.7 });
  }

  // ── Status LEDs ───────────────────────────────────────────────
  const ledColors = [0xDD2233, 0xffaa20, 0x44ff44];
  for (let i = 0; i < 3; i++) {
    const blink = Math.sin(t * (3 + i * 1.7) + i * 2.1) > 0 ? 0.9 : 0.08;
    const lx = -s * 0.15 + i * s * 0.12;
    const ly = -s * 0.40;
    g.circle(lx, ly + s * 0.003, s * 0.04);
    g.fill({ color: 0x000000, alpha: 0.5 });
    g.circle(lx, ly, s * 0.042);
    g.fill({ color: ledColors[i]!, alpha: blink * 0.18 });
    g.circle(lx, ly, s * 0.025);
    g.fill({ color: ledColors[i]!, alpha: blink });
    g.circle(lx - s * 0.008, ly - s * 0.008, s * 0.009);
    g.fill({ color: 0xffffff, alpha: blink * 0.75 });
  }
}

// ─── Weapon drawing helpers ───────────────────────────────────

/** Ballistic: multi-barrel autocannon with clamp bands */
function drawWeaponBallistic(g: Graphics, s: number, t: number, tier: number, yOff: number, xOff: number = 0, cd: number = 0) {
  const y = s * yOff;
  const dx = s * xOff;
  const col = WEAPON_COLORS.ballistic;
  const barrelCount = tier >= 3 ? 3 : 2;
  const barrelSpacing = s * 0.055;
  const barrelH = s * 0.055;
  const barrelLen = s * 0.58;
  const startX = s * 0.32 + dx;
  const charge = 1 - cd; // 0 = cooling, 1 = ready

  // Barrel mount bracket
  g.roundRect(s * 0.22 + dx, y - s * 0.10, s * 0.14, s * 0.20, 2);
  g.fill(BARREL_MOUNT_COLOR);
  g.stroke({ color: 0x1a1e28, width: 0.7 });

  // Individual barrel tubes
  for (let i = 0; i < barrelCount; i++) {
    const by = y + (i - (barrelCount - 1) / 2) * barrelSpacing;
    // Barrel tube
    g.roundRect(startX, by - barrelH / 2, barrelLen, barrelH, 1);
    g.fill(BARREL_COLOR);
    g.stroke({ color: 0x303848, width: 0.6 });
    // Top highlight
    g.roundRect(startX + 1, by - barrelH / 2, barrelLen - 2, barrelH * 0.22, 1);
    g.fill({ color: 0xffffff, alpha: 0.08 });
  }

  // Barrel heat glow — barrels glow orange-red right after firing, fading as they cool
  if (cd > 0.05) {
    const heatFlicker = 0.8 + Math.sin(t * 20 + yOff * 7) * 0.2;
    for (let i = 0; i < barrelCount; i++) {
      const by = y + (i - (barrelCount - 1) / 2) * barrelSpacing;
      // Full barrel heat overlay
      g.roundRect(startX, by - barrelH * 0.7, barrelLen, barrelH * 1.4, 1);
      g.fill({ color: 0xff2200, alpha: cd * 0.45 * heatFlicker });
      // Bright muzzle-end hotspot
      g.roundRect(startX + barrelLen * 0.5, by - barrelH * 0.9, barrelLen * 0.5, barrelH * 1.8, 1);
      g.fill({ color: 0xff6600, alpha: cd * 0.5 * heatFlicker });
      // White-hot core at muzzle tip
      g.circle(startX + barrelLen - s * 0.02, by, barrelH * 0.6);
      g.fill({ color: 0xffaa44, alpha: cd * 0.6 * heatFlicker });
    }
    // Outer heat haze around entire barrel assembly
    const hazeH = barrelSpacing * (barrelCount - 1) + barrelH * 3;
    g.roundRect(startX + barrelLen * 0.3, y - hazeH / 2, barrelLen * 0.7, hazeH, 2);
    g.fill({ color: 0xff4400, alpha: cd * 0.12 });
  }

  // Clamp bands (gold-tinted)
  for (const bx of [0.42, 0.56, 0.70]) {
    const bandY = y - (barrelCount - 1) / 2 * barrelSpacing - barrelH / 2 - s * 0.01;
    const bandH = barrelSpacing * (barrelCount - 1) + barrelH + s * 0.02;
    g.rect(s * bx + dx, bandY, s * 0.022, bandH);
    g.fill(col);
    g.stroke({ color: 0x1e2230, width: 0.4 });
    // Band highlight
    g.rect(s * bx + dx, bandY, s * 0.022, s * 0.02);
    g.fill({ color: 0xffffff, alpha: 0.2 });
  }

  // Muzzle brake
  const muzzleX = startX + barrelLen - s * 0.02;
  const muzzleH = barrelSpacing * (barrelCount - 1) + barrelH + s * 0.04;
  g.rect(muzzleX, y - muzzleH / 2, s * 0.10, muzzleH);
  g.fill(MUZZLE_COLOR);
  g.stroke({ color: 0x202430, width: 0.5 });

  // Muzzle bore holes
  for (let i = 0; i < barrelCount; i++) {
    const by = y + (i - (barrelCount - 1) / 2) * barrelSpacing;
    g.circle(muzzleX + s * 0.08, by, barrelH * 0.32);
    g.fill(0x060810);
  }

  // Ammo feed indicator — fills from left to right as weapon charges
  const feedLen = s * 0.18;
  const feedH = s * 0.035;
  const feedX = s * 0.22 + dx;
  const feedY = y + s * 0.085;
  // Background track
  g.roundRect(feedX, feedY, feedLen, feedH, 2);
  g.fill({ color: 0x0a0e14, alpha: 0.8 });
  g.stroke({ color: 0x303848, width: 0.5 });
  // Fill bar
  if (charge > 0.02) {
    g.roundRect(feedX + 1, feedY + 1, (feedLen - 2) * charge, feedH - 2, 1);
    g.fill({ color: col, alpha: 0.7 + charge * 0.3 });
    // Bright leading edge
    const edgeX = feedX + 1 + (feedLen - 2) * charge;
    g.rect(edgeX - s * 0.008, feedY + 1, s * 0.008, feedH - 2);
    g.fill({ color: 0xffffff, alpha: 0.8 });
  }
  // Ready flash when fully charged — big pulsing glow
  if (charge > 0.9) {
    const flash = 0.5 + Math.sin(t * 14 + yOff * 10) * 0.5;
    g.roundRect(feedX - s * 0.01, feedY - s * 0.01, feedLen + s * 0.02, feedH + s * 0.02, 3);
    g.fill({ color: col, alpha: flash * 0.35 });
    g.roundRect(feedX - s * 0.02, feedY - s * 0.02, feedLen + s * 0.04, feedH + s * 0.04, 4);
    g.fill({ color: col, alpha: flash * 0.12 });
  }

  // Ammo indicator LED — big, bright when ready
  const ledR = s * 0.022;
  const ledAlpha = charge > 0.9 ? (0.7 + Math.sin(t * 10 + yOff * 10) * 0.3) : charge * 0.4;
  g.circle(s * 0.26 + dx, y - s * 0.075, ledR);
  g.fill({ color: col, alpha: ledAlpha });
  // LED glow halo
  if (charge > 0.8) {
    g.circle(s * 0.26 + dx, y - s * 0.075, ledR * 2.5);
    g.fill({ color: col, alpha: (charge - 0.8) / 0.2 * 0.2 });
  }

  // Muzzle glow when ready — barrels light up at the tips
  if (charge > 0.85) {
    const muzzleGlow = (charge - 0.85) / 0.15;
    const mgPulse = 0.6 + Math.sin(t * 10 + yOff * 8) * 0.4;
    for (let i = 0; i < barrelCount; i++) {
      const by = y + (i - (barrelCount - 1) / 2) * barrelSpacing;
      g.circle(muzzleX + s * 0.08, by, barrelH * 0.7);
      g.fill({ color: col, alpha: muzzleGlow * mgPulse * 0.4 });
      g.circle(muzzleX + s * 0.08, by, barrelH * 0.4);
      g.fill({ color: 0xffffff, alpha: muzzleGlow * mgPulse * 0.3 });
    }
  }
}

/** Energy: sleek tapered beam emitter with glowing tip */
function drawWeaponEnergy(g: Graphics, s: number, t: number, tier: number, yOff: number, xOff: number = 0, cd: number = 0) {
  const y = s * yOff;
  const dx = s * xOff;
  const col = WEAPON_COLORS.energy;
  const charge = 1 - cd; // 0 = discharged, 1 = fully charged

  // Barrel mount
  g.roundRect(s * 0.22 + dx, y - s * 0.09, s * 0.13, s * 0.18, 2);
  g.fill(BARREL_MOUNT_COLOR);
  g.stroke({ color: 0x1a1e28, width: 0.7 });

  // Tapered barrel — wider at base, narrower at tip (drawn as polygon)
  const baseH = s * 0.14;
  const tipH = s * 0.07;
  const bx0 = s * 0.30 + dx;
  const bx1 = s * 0.84 + dx;
  g.moveTo(bx0, y - baseH / 2);
  g.lineTo(bx1, y - tipH / 2);
  g.lineTo(bx1, y + tipH / 2);
  g.lineTo(bx0, y + baseH / 2);
  g.closePath();
  g.fill(BARREL_COLOR);
  g.stroke({ color: 0x303848, width: 0.7 });

  // Top highlight
  g.moveTo(bx0 + 1, y - baseH / 2 + 1);
  g.lineTo(bx1 - 1, y - tipH / 2 + 1);
  g.lineTo(bx1 - 1, y - tipH / 2 + s * 0.025);
  g.lineTo(bx0 + 1, y - baseH / 2 + s * 0.03);
  g.closePath();
  g.fill({ color: 0xffffff, alpha: 0.08 });

  // Capacitor fins (tier 3+) — glow with charge
  if (tier >= 3) {
    for (const side of [-1, 1]) {
      const finY = y + side * (baseH / 2 + s * 0.02);
      g.moveTo(s * 0.40 + dx, finY);
      g.lineTo(s * 0.55 + dx, finY + side * s * 0.06);
      g.lineTo(s * 0.55 + dx, finY);
      g.closePath();
      g.fill({ color: col, alpha: 0.25 + charge * 0.55 });
      // Capacitor charge glow on fins — bright outline + halo
      if (charge > 0.3) {
        const finGlow = (charge - 0.3) / 0.7;
        g.moveTo(s * 0.40 + dx, finY);
        g.lineTo(s * 0.55 + dx, finY + side * s * 0.06);
        g.lineTo(s * 0.55 + dx, finY);
        g.closePath();
        g.stroke({ color: col, width: 1.8 + finGlow * 1.5, alpha: finGlow * 0.8 });
        // Fin glow halo
        g.moveTo(s * 0.40 + dx, finY + side * s * 0.01);
        g.lineTo(s * 0.55 + dx, finY + side * (s * 0.06 + s * 0.02));
        g.lineTo(s * 0.55 + dx, finY + side * s * 0.01);
        g.closePath();
        g.fill({ color: col, alpha: finGlow * 0.2 });
      }
    }
  }

  // Barrel interior glow — entire barrel lights up from inside as it charges
  if (charge > 0.05) {
    g.moveTo(bx0, y - baseH / 2);
    g.lineTo(bx0 + (bx1 - bx0) * charge, y - (baseH / 2 + (tipH / 2 - baseH / 2) * charge));
    g.lineTo(bx0 + (bx1 - bx0) * charge, y + (baseH / 2 + (tipH / 2 - baseH / 2) * charge));
    g.lineTo(bx0, y + baseH / 2);
    g.closePath();
    g.fill({ color: col, alpha: charge * 0.2 });
  }

  // Energy channel line — thick, bright, fills base to tip
  const channelWidth = 1.5 + charge * 2.5;
  const channelAlpha = 0.2 + charge * 0.7;
  g.moveTo(bx0 + s * 0.04, y);
  g.lineTo(bx0 + s * 0.04 + (bx1 - bx0 - s * 0.06) * charge, y);
  g.stroke({ color: col, width: channelWidth, alpha: channelAlpha });
  // Bright white core line
  if (charge > 0.3) {
    g.moveTo(bx0 + s * 0.04, y);
    g.lineTo(bx0 + s * 0.04 + (bx1 - bx0 - s * 0.06) * charge, y);
    g.stroke({ color: 0xffffff, width: channelWidth * 0.4, alpha: (charge - 0.3) * 0.5 });
  }

  // Traveling energy nodes along barrel — bigger, brighter, with halos
  if (charge > 0.1 && charge < 0.95) {
    const nodeCount = tier >= 3 ? 4 : 3;
    for (let i = 0; i < nodeCount; i++) {
      const nodePhase = (t * 5 + i * (1 / nodeCount) + yOff * 5) % 1;
      if (nodePhase > charge) continue;
      const nodeX = bx0 + s * 0.04 + (bx1 - bx0 - s * 0.06) * nodePhase;
      const halfH = baseH / 2 + (tipH / 2 - baseH / 2) * nodePhase;
      // Outer halo
      g.circle(nodeX, y, s * 0.045);
      g.fill({ color: col, alpha: 0.2 });
      // Bright node
      g.circle(nodeX, y, s * 0.028);
      g.fill({ color: col, alpha: 0.7 });
      g.circle(nodeX, y, s * 0.014);
      g.fill({ color: 0xffffff, alpha: 0.8 });
      // Perpendicular spark lines — full barrel width
      g.moveTo(nodeX, y - halfH * 0.8);
      g.lineTo(nodeX, y + halfH * 0.8);
      g.stroke({ color: col, width: 1.2, alpha: 0.5 });
    }
  }

  // Glowing emitter tip — much larger, scales dramatically with charge
  const tipX = bx1 + s * 0.02;
  const tipPulse = 0.7 + Math.sin(t * 8 + yOff * 6) * 0.3;
  // Huge outer halo
  g.circle(tipX, y, s * (0.05 + charge * 0.12));
  g.fill({ color: col, alpha: (0.1 + charge * 0.3) * tipPulse });
  // Mid glow
  g.circle(tipX, y, s * (0.035 + charge * 0.065));
  g.fill({ color: col, alpha: (0.2 + charge * 0.5) * tipPulse });
  // Inner glow
  g.circle(tipX, y, s * (0.02 + charge * 0.035));
  g.fill({ color: 0xaaeeff, alpha: (0.3 + charge * 0.5) * tipPulse });
  // Hot white core
  g.circle(tipX, y, s * (0.008 + charge * 0.02));
  g.fill({ color: 0xffffff, alpha: (0.4 + charge * 0.6) * tipPulse });

  // Ready state — big crackling energy field around emitter
  if (charge > 0.8) {
    const ready = (charge - 0.8) / 0.2;
    const crackle1 = 0.5 + Math.sin(t * 22 + yOff * 8) * 0.5;
    const crackle2 = 0.5 + Math.sin(t * 17 + yOff * 12) * 0.5;
    // Large energy field
    g.circle(tipX, y, s * 0.16);
    g.fill({ color: col, alpha: ready * crackle1 * 0.15 });
    // Electric arc rings
    g.circle(tipX, y, s * 0.12);
    g.stroke({ color: col, width: 1.5, alpha: ready * crackle1 * 0.5 });
    g.circle(tipX, y, s * 0.08);
    g.stroke({ color: 0xffffff, width: 1.0, alpha: ready * crackle2 * 0.6 });
    // Stray sparks
    for (let i = 0; i < 3; i++) {
      const sa = t * (8 + i * 3) + i * 2.1 + yOff * 4;
      const sr = s * (0.06 + Math.sin(sa * 2) * 0.04);
      g.circle(tipX + Math.cos(sa) * sr, y + Math.sin(sa) * sr, s * 0.01);
      g.fill({ color: 0xffffff, alpha: ready * crackle2 * 0.7 });
    }
  }
}

/** Rocket: stubby rectangular missile pod with tube openings */
function drawWeaponRocket(g: Graphics, s: number, t: number, tier: number, yOff: number, xOff: number = 0, cd: number = 0) {
  const y = s * yOff;
  const dx = s * xOff;
  const col = WEAPON_COLORS.rocket;
  const charge = 1 - cd; // 0 = empty, 1 = loaded

  // Mount bracket
  g.roundRect(s * 0.22 + dx, y - s * 0.10, s * 0.12, s * 0.20, 2);
  g.fill(BARREL_MOUNT_COLOR);
  g.stroke({ color: 0x1a1e28, width: 0.7 });

  // Pod body — boxy and shorter
  const podW = s * 0.44;
  const podH = s * 0.18;
  g.roundRect(s * 0.30 + dx, y - podH / 2, podW, podH, 2);
  g.fill(BARREL_COLOR);
  g.stroke({ color: 0x303848, width: 0.7 });

  // Top highlight
  g.roundRect(s * 0.31 + dx, y - podH / 2, podW - 2, podH * 0.18, 1);
  g.fill({ color: 0xffffff, alpha: 0.08 });

  // Orange stripe along top — pulses strongly when loading
  const stripePulse = cd > 0.05 ? (0.4 + charge * 0.4 + Math.sin(t * 8) * 0.2) : 0.6;
  g.rect(s * 0.34 + dx, y - podH / 2 + 1, podW - s * 0.08, s * 0.03);
  g.fill({ color: col, alpha: stripePulse });
  // Stripe glow
  if (cd > 0.05) {
    g.rect(s * 0.34 + dx, y - podH / 2 - s * 0.005, podW - s * 0.08, s * 0.04);
    g.fill({ color: col, alpha: stripePulse * 0.2 });
  }

  // Pod interior loading glow — whole pod illuminates from inside
  if (charge > 0.1 && cd > 0.05) {
    g.roundRect(s * 0.31 + dx, y - podH / 2 + 1, podW - 2, podH - 2, 2);
    g.fill({ color: col, alpha: charge * 0.15 });
  }

  // Missile loading glow — fills tubes from back to front as missile loads
  const tubes = tier >= 3 ? 2 : 1;
  const tubeSpacing = s * 0.065;
  const tubeOpenX = s * 0.30 + dx + podW - s * 0.01;
  const podStartX = s * 0.33 + dx;

  for (let i = 0; i < tubes; i++) {
    const ty = y + (i - (tubes - 1) / 2) * tubeSpacing;

    // Missile body loading from back to front — bright and visible
    if (charge > 0.05) {
      const missileLen = (tubeOpenX - podStartX) * charge;
      const missileH = s * 0.055;
      // Missile outer glow trail
      g.roundRect(podStartX, ty - missileH * 0.7, missileLen, missileH * 1.4, 2);
      g.fill({ color: col, alpha: charge * 0.15 });
      // Missile body silhouette
      g.roundRect(podStartX, ty - missileH / 2, missileLen, missileH, 2);
      g.fill({ color: col, alpha: 0.25 + charge * 0.35 });
      // Bright white core
      g.roundRect(podStartX + s * 0.01, ty - missileH * 0.25, missileLen - s * 0.02, missileH * 0.5, 1);
      g.fill({ color: 0xffcc88, alpha: charge * 0.4 });
      // Missile nose cone glow — big and bright
      const noseX = podStartX + missileLen;
      g.circle(noseX, ty, s * 0.03);
      g.fill({ color: col, alpha: charge * 0.5 });
      g.circle(noseX, ty, s * 0.016);
      g.fill({ color: 0xffffff, alpha: charge * 0.6 });
    }

    // Dark bore
    g.circle(tubeOpenX, ty, s * 0.038);
    g.fill(0x0a0e14);
    // Inner ring
    g.circle(tubeOpenX, ty, s * 0.028);
    g.stroke({ color: 0x2a3040, width: 0.6 });

    // Tube interior glow when missile reaches the front
    if (charge > 0.6) {
      const tubeGlow = (charge - 0.6) / 0.4;
      const tgPulse = 0.6 + Math.sin(t * 6 + i * 3 + yOff * 8) * 0.4;
      // Big bore glow
      g.circle(tubeOpenX, ty, s * 0.035);
      g.fill({ color: col, alpha: tubeGlow * 0.5 * tgPulse });
      g.circle(tubeOpenX, ty, s * 0.05);
      g.fill({ color: col, alpha: tubeGlow * 0.15 * tgPulse });
    }

    // Exhaust ready glow — missile tip visible, pulsing hot
    if (charge > 0.85) {
      const ready = (charge - 0.85) / 0.15;
      const readyPulse = 0.5 + Math.sin(t * 10 + i * 3 + yOff * 10) * 0.5;
      g.circle(tubeOpenX, ty, s * 0.028);
      g.fill({ color: col, alpha: ready * readyPulse * 0.7 });
      g.circle(tubeOpenX, ty, s * 0.014);
      g.fill({ color: 0xffffff, alpha: ready * readyPulse * 0.6 });
      // Exhaust heat shimmer ahead of tube
      g.circle(tubeOpenX + s * 0.03, ty, s * 0.025);
      g.fill({ color: col, alpha: ready * readyPulse * 0.2 });
    }
  }

  // Front plate
  g.rect(s * 0.30 + dx + podW - s * 0.025, y - podH / 2 + 1, s * 0.025, podH - 2);
  g.fill({ color: 0x3a4050, alpha: 0.6 });

  // Ready LED — big, bright solid when loaded, pulses during loading
  const ledR = s * 0.02;
  const ledAlpha = charge > 0.9
    ? (0.7 + Math.sin(t * 6 + yOff * 10) * 0.3)
    : charge * 0.35;
  g.circle(s * 0.34 + dx, y + podH / 2 - s * 0.03, ledR);
  g.fill({ color: col, alpha: ledAlpha });
  // LED glow
  if (charge > 0.7) {
    g.circle(s * 0.34 + dx, y + podH / 2 - s * 0.03, ledR * 2.5);
    g.fill({ color: col, alpha: (charge - 0.7) / 0.3 * 0.25 });
  }
}

/** Bomb: short wide mortar tube with open bore */
function drawWeaponBomb(g: Graphics, s: number, t: number, tier: number, yOff: number, xOff: number = 0, cd: number = 0) {
  const y = s * yOff;
  const dx = s * xOff;
  const col = WEAPON_COLORS.bomb;
  const charge = 1 - cd; // 0 = empty, 1 = shell loaded

  // Mount bracket
  g.roundRect(s * 0.22 + dx, y - s * 0.11, s * 0.12, s * 0.22, 2);
  g.fill(BARREL_MOUNT_COLOR);
  g.stroke({ color: 0x1a1e28, width: 0.7 });

  // Mortar tube — short and wide
  const tubeLen = s * 0.32;
  const tubeH = s * 0.20;
  g.roundRect(s * 0.30 + dx, y - tubeH / 2, tubeLen, tubeH, 3);
  g.fill(BARREL_COLOR);
  g.stroke({ color: 0x303848, width: 0.7 });

  // Inner dark tube bore (visible from the front)
  g.roundRect(s * 0.32 + dx, y - tubeH / 2 + s * 0.025, tubeLen - s * 0.04, tubeH - s * 0.05, 2);
  g.fill({ color: 0x1a1e24, alpha: 0.5 });

  // Tube interior charge glow — whole bore illuminates as shell loads
  const boreX = s * 0.30 + dx + tubeLen;
  if (charge > 0.05 && cd > 0.05) {
    const tubeGlowAlpha = charge * 0.25;
    g.roundRect(s * 0.32 + dx, y - tubeH / 2 + s * 0.025, tubeLen - s * 0.04, tubeH - s * 0.05, 2);
    g.fill({ color: col, alpha: tubeGlowAlpha });
  }

  // Shell loading — big glowing orb rises inside the bore
  if (charge > 0.1) {
    const shellDepth = (1 - charge) * tubeLen * 0.6;
    const shellX = boreX - shellDepth;
    const shellR = tubeH * 0.25 * Math.min(1, charge * 1.3);
    // Outer glow halo
    g.circle(shellX, y, shellR + s * 0.06);
    g.fill({ color: col, alpha: charge * 0.15 });
    // Mid glow
    g.circle(shellX, y, shellR + s * 0.025);
    g.fill({ color: col, alpha: charge * 0.3 });
    // Shell body
    g.circle(shellX, y, shellR);
    g.fill({ color: col, alpha: charge * 0.55 });
    // Hot core — bright yellow/white
    if (charge > 0.35) {
      const coreBright = (charge - 0.35) / 0.65;
      g.circle(shellX, y, shellR * 0.55);
      g.fill({ color: 0xffcc44, alpha: coreBright * 0.7 });
      g.circle(shellX, y, shellR * 0.25);
      g.fill({ color: 0xffffff, alpha: coreBright * 0.5 });
    }
  }

  // Bore opening circle at front
  g.circle(boreX, y, tubeH * 0.42);
  g.fill(0x0a0e14);
  g.circle(boreX, y, tubeH * 0.32);
  g.stroke({ color: 0x2a3040, width: 0.8 });

  // Loading ring — thick arc that sweeps closed, rotating
  if (cd > 0.05) {
    const arcAngle = charge * Math.PI * 2;
    const ringR = tubeH * 0.45;
    const segments = 20;
    const startAngle = t * 3 + yOff * 5;
    const ringWidth = 2.5 + charge * 1.5;
    for (let i = 0; i < segments; i++) {
      const segAngle = (i / segments) * Math.PI * 2;
      if (segAngle > arcAngle) break;
      const nextSegAngle = ((i + 1) / segments) * Math.PI * 2;
      if (nextSegAngle > arcAngle) break;
      const a0 = startAngle + segAngle;
      const a1 = startAngle + nextSegAngle;
      g.moveTo(boreX + Math.cos(a0) * ringR, y + Math.sin(a0) * ringR);
      g.lineTo(boreX + Math.cos(a1) * ringR, y + Math.sin(a1) * ringR);
      g.stroke({ color: col, width: ringWidth, alpha: 0.4 + charge * 0.5 });
    }
    // Bright leading dot on the ring
    const leadAngle = startAngle + arcAngle;
    g.circle(boreX + Math.cos(leadAngle) * ringR, y + Math.sin(leadAngle) * ringR, s * 0.02);
    g.fill({ color: 0xffffff, alpha: 0.6 + charge * 0.3 });
  }

  // Bore glow when loaded — big pulsing ready indicator
  if (charge > 0.75) {
    const ready = (charge - 0.75) / 0.25;
    const readyPulse = 0.5 + Math.sin(t * 6 + yOff * 10) * 0.5;
    g.circle(boreX, y, tubeH * 0.5);
    g.fill({ color: col, alpha: ready * readyPulse * 0.15 });
    g.circle(boreX, y, tubeH * 0.35);
    g.fill({ color: col, alpha: ready * readyPulse * 0.35 });
    g.circle(boreX, y, tubeH * 0.18);
    g.fill({ color: 0xffcc44, alpha: ready * readyPulse * 0.4 });
  }

  // Danger stripe — pulses prominently during loading
  const stripeW = s * 0.045;
  const stripeBase = cd > 0.05 ? (0.35 + Math.sin(t * 5 + yOff * 8) * 0.3) : 0.5;
  for (let i = 0; i < 3; i++) {
    const sx = s * 0.34 + dx + i * stripeW * 2;
    g.rect(sx, y + tubeH / 2 - s * 0.035, stripeW, s * 0.035);
    g.fill({ color: col, alpha: stripeBase });
    // Stripe glow
    if (cd > 0.05) {
      g.rect(sx, y + tubeH / 2 - s * 0.04, stripeW, s * 0.045);
      g.fill({ color: col, alpha: stripeBase * 0.2 });
    }
  }

  // Targeting scope (tier 3+) — pulses bright when locking on
  if (tier >= 3) {
    g.circle(s * 0.42 + dx, y - tubeH / 2 - s * 0.02, s * 0.03);
    g.fill(0x2d323f);
    g.stroke({ color: 0x1a1e28, width: 0.5 });
    const scopeAlpha = charge > 0.7 ? (0.6 + Math.sin(t * 8) * 0.4) : 0.2 + charge * 0.3;
    g.circle(s * 0.42 + dx, y - tubeH / 2 - s * 0.02, s * 0.016);
    g.fill({ color: col, alpha: scopeAlpha });
    // Scope glow halo
    if (charge > 0.7) {
      g.circle(s * 0.42 + dx, y - tubeH / 2 - s * 0.02, s * 0.04);
      g.fill({ color: col, alpha: (charge - 0.7) / 0.3 * 0.25 });
    }
  }

  // Loading indicator glow — bigger, tied to charge
  g.circle(boreX, y, tubeH * 0.28);
  g.fill({ color: col, alpha: charge * 0.3 });
}

/** Melee: mechanical blade arm with shoulder joint */
function drawWeaponMelee(g: Graphics, s: number, t: number, tier: number, yOff: number, xOff: number = 0, cd: number = 0) {
  const y = s * yOff;
  const dx = s * xOff;
  const col = WEAPON_COLORS.melee;
  const charge = 1 - cd; // 0 = just struck, 1 = ready to strike

  // Shoulder joint — pulses with charge
  g.circle(s * 0.26 + dx, y, s * 0.065);
  g.fill(BARREL_MOUNT_COLOR);
  g.stroke({ color: 0x1a1e28, width: 0.7 });
  g.circle(s * 0.26 + dx, y, s * 0.035);
  g.fill(AXLE_COLOR);
  // Shoulder energy ring — big, bright
  if (charge > 0.2) {
    const shoulderGlow = (charge - 0.2) / 0.8;
    const sPulse = 0.6 + Math.sin(t * 8 + yOff * 6) * 0.4;
    g.circle(s * 0.26 + dx, y, s * 0.08);
    g.fill({ color: col, alpha: shoulderGlow * 0.15 * sPulse });
    g.circle(s * 0.26 + dx, y, s * 0.065);
    g.stroke({ color: col, width: 1.5 + shoulderGlow * 1.5, alpha: shoulderGlow * 0.6 * sPulse });
  }

  // Arm segment — energy vein running along it
  const armLen = s * 0.34;
  const armH = s * 0.07;
  g.roundRect(s * 0.30 + dx, y - armH / 2, armLen, armH, 2);
  g.fill(BARREL_COLOR);
  g.stroke({ color: 0x303848, width: 0.6 });

  // Arm highlight
  g.roundRect(s * 0.31 + dx, y - armH / 2 + 0.5, armLen - 2, armH * 0.25, 1);
  g.fill({ color: 0xffffff, alpha: 0.08 });

  // Energy vein along arm — thick, bright, fills from shoulder to wrist
  if (charge > 0.05) {
    const veinLen = armLen * charge;
    const veinPulse = 0.5 + Math.sin(t * 10 + yOff * 8) * 0.3;
    const veinWidth = 2.0 + charge * 2.0;
    // Outer glow
    g.moveTo(s * 0.30 + dx, y);
    g.lineTo(s * 0.30 + dx + veinLen, y);
    g.stroke({ color: col, width: veinWidth + 3, alpha: veinPulse * charge * 0.2 });
    // Main vein
    g.moveTo(s * 0.30 + dx, y);
    g.lineTo(s * 0.30 + dx + veinLen, y);
    g.stroke({ color: col, width: veinWidth, alpha: veinPulse * 0.7 });
    // Bright core
    g.moveTo(s * 0.30 + dx, y);
    g.lineTo(s * 0.30 + dx + veinLen, y);
    g.stroke({ color: 0xffffff, width: veinWidth * 0.3, alpha: charge * 0.4 });
    // Traveling sparks — multiple, bigger
    const sparkCount = charge < 0.9 ? 2 : 0;
    for (let si = 0; si < sparkCount; si++) {
      const sparkPhase = (t * (5 + si * 2) + yOff * 4 + si * 0.5) % 1;
      if (sparkPhase < charge) {
        const sparkX = s * 0.30 + dx + armLen * sparkPhase;
        g.circle(sparkX, y, s * 0.025);
        g.fill({ color: col, alpha: 0.4 });
        g.circle(sparkX, y, s * 0.014);
        g.fill({ color: 0xffffff, alpha: 0.7 });
      }
    }
  }

  // Wrist joint — glows prominently when charged
  const wx = s * 0.30 + dx + armLen;
  g.circle(wx, y, s * 0.04);
  g.fill(AXLE_COLOR);
  g.stroke({ color: 0x1a1e28, width: 0.5 });
  if (charge > 0.4) {
    const wristGlow = (charge - 0.4) / 0.6;
    const wPulse = 0.6 + Math.sin(t * 8 + yOff * 10) * 0.4;
    g.circle(wx, y, s * 0.055);
    g.fill({ color: col, alpha: wristGlow * 0.25 * wPulse });
    g.circle(wx, y, s * 0.04);
    g.fill({ color: col, alpha: wristGlow * 0.5 * wPulse });
  }

  // Blade / hammerhead
  // Oscillation intensifies dramatically — weapon shakes eagerly when charged
  const oscBase = s * 0.012;
  const oscIntensity = oscBase * (0.2 + charge * 1.2);
  const oscSpeed = 4 + charge * 14; // much faster vibration when charged
  const osc = Math.sin(t * oscSpeed + yOff * 8) * oscIntensity;

  if (tier >= 3) {
    // Hammerhead — wide blunt striker
    const hx = wx + s * 0.02;
    const hw = s * 0.14;
    const hh = s * 0.14;
    g.roundRect(hx + osc, y - hh / 2, hw, hh, 2);
    g.fill({ color: 0x5a6070 });
    g.stroke({ color: 0x1a1e28, width: 0.6 });
    // Impact face highlight — very bright with charge
    g.rect(hx + hw - s * 0.025 + osc, y - hh / 2 + 1, s * 0.025, hh - 2);
    g.fill({ color: col, alpha: 0.3 + charge * 0.7 });
    // Energy glow outline — thick and bright
    const eg = charge * (0.4 + Math.sin(t * 7 + yOff * 12) * 0.3);
    g.roundRect(hx + osc, y - hh / 2, hw, hh, 2);
    g.stroke({ color: col, width: 1.0 + charge * 2.5, alpha: eg * 0.8 });
    // Charged halo — big energy field around hammerhead
    if (charge > 0.6) {
      const halo = (charge - 0.6) / 0.4;
      const haloPulse = 0.5 + Math.sin(t * 10 + yOff * 8) * 0.5;
      g.roundRect(hx + osc - s * 0.03, y - hh / 2 - s * 0.03, hw + s * 0.06, hh + s * 0.06, 4);
      g.fill({ color: col, alpha: halo * haloPulse * 0.15 });
      g.roundRect(hx + osc - s * 0.015, y - hh / 2 - s * 0.015, hw + s * 0.03, hh + s * 0.03, 3);
      g.fill({ color: col, alpha: halo * haloPulse * 0.25 });
      // Impact face white-hot glow when fully charged
      if (charge > 0.9) {
        const hot = (charge - 0.9) / 0.1;
        g.rect(hx + hw - s * 0.03 + osc, y - hh / 2 + 1, s * 0.03, hh - 2);
        g.fill({ color: 0xffffff, alpha: hot * haloPulse * 0.4 });
      }
    }
  } else {
    // Triangular blade
    const bx = wx + s * 0.02;
    g.moveTo(bx + osc, y - s * 0.055);
    g.lineTo(bx + s * 0.18 + osc, y);
    g.lineTo(bx + osc, y + s * 0.055);
    g.closePath();
    g.fill({ color: 0x8090a8 });
    g.stroke({ color: 0x1a1e28, width: 0.6 });
    // Blade edge highlight — very bright when charged
    g.moveTo(bx + s * 0.02 + osc, y - s * 0.035);
    g.lineTo(bx + s * 0.15 + osc, y);
    g.stroke({ color: col, width: 1.0 + charge * 2.0, alpha: 0.3 + charge * 0.7 });
    // Energy edge glow — thick, bright outline
    const eg = charge * (0.4 + Math.sin(t * 7 + yOff * 12) * 0.3);
    g.moveTo(bx + osc, y - s * 0.055);
    g.lineTo(bx + s * 0.18 + osc, y);
    g.lineTo(bx + osc, y + s * 0.055);
    g.closePath();
    g.stroke({ color: col, width: 1.0 + charge * 2.0, alpha: eg * 0.8 });
    // Charged blade glow — visible energy field
    if (charge > 0.6) {
      const bladeGlow = (charge - 0.6) / 0.4;
      const bgPulse = 0.5 + Math.sin(t * 10 + yOff * 8) * 0.5;
      // Outer halo
      g.moveTo(bx + osc - s * 0.02, y - s * 0.075);
      g.lineTo(bx + s * 0.22 + osc, y);
      g.lineTo(bx + osc - s * 0.02, y + s * 0.075);
      g.closePath();
      g.fill({ color: col, alpha: bladeGlow * bgPulse * 0.15 });
      // Inner fill
      g.moveTo(bx + osc, y - s * 0.055);
      g.lineTo(bx + s * 0.18 + osc, y);
      g.lineTo(bx + osc, y + s * 0.055);
      g.closePath();
      g.fill({ color: col, alpha: bladeGlow * bgPulse * 0.25 });
      // White-hot edge when fully charged
      if (charge > 0.9) {
        const hot = (charge - 0.9) / 0.1;
        g.moveTo(bx + s * 0.02 + osc, y - s * 0.04);
        g.lineTo(bx + s * 0.16 + osc, y);
        g.stroke({ color: 0xffffff, width: 1.5, alpha: hot * bgPulse * 0.5 });
      }
    }
  }
}

/** Empty hardpoint: dark bracket with bolt dots */
function drawEmptyHardpoint(g: Graphics, s: number, yOff: number, xOff: number = 0) {
  const y = s * yOff;
  const dx = s * xOff;

  // Mount plate
  g.roundRect(s * 0.22 + dx, y - s * 0.06, s * 0.14, s * 0.12, 2);
  g.fill({ color: BARREL_MOUNT_COLOR, alpha: 0.5 });
  g.stroke({ color: 0x1a1e28, width: 0.5, alpha: 0.4 });

  // Mount pin stub
  g.roundRect(s * 0.32 + dx, y - s * 0.025, s * 0.08, s * 0.05, 1);
  g.fill({ color: BARREL_COLOR, alpha: 0.35 });

  // Bolt dots
  drawBolt(g, s * 0.26 + dx, y - s * 0.03, s * 0.018);
  drawBolt(g, s * 0.26 + dx, y + s * 0.03, s * 0.018);
}

/** Dispatch to the correct weapon draw function */
function drawWeaponSlot(g: Graphics, s: number, t: number, slot: WeaponSlotRenderInfo | null, offset: SlotOffset) {
  if (!slot) {
    drawEmptyHardpoint(g, s, offset.y, offset.x);
    return;
  }
  const cd = slot.cooldownProgress;
  switch (slot.category) {
    case 'ballistic': drawWeaponBallistic(g, s, t, slot.tier, offset.y, offset.x, cd); break;
    case 'energy':    drawWeaponEnergy(g, s, t, slot.tier, offset.y, offset.x, cd); break;
    case 'rocket':    drawWeaponRocket(g, s, t, slot.tier, offset.y, offset.x, cd); break;
    case 'bomb':      drawWeaponBomb(g, s, t, slot.tier, offset.y, offset.x, cd); break;
    case 'melee':     drawWeaponMelee(g, s, t, slot.tier, offset.y, offset.x, cd); break;
  }
}

// ─── Turret ────────────────────────────────────────────────────

/**
 * Draw a single side hardpoint weapon at origin (0,0).
 * The Graphics container should be positioned at the slot offset and rotated independently.
 */
export function drawSideHardpointWeapon(
  g: Graphics,
  s: number,
  t: number,
  slot: WeaponSlotRenderInfo | null,
) {
  const zeroOffset: SlotOffset = { x: 0, y: 0 };
  drawWeaponSlot(g, s, t, slot, zeroOffset);
}

export function drawRobotTurret(
  g: Graphics,
  s: number,
  t: number = 0,
  stateColor: number = 0xDD2233,
  weaponSlots?: (WeaponSlotRenderInfo | null)[],
  sideSlotIndices?: number[],
) {
  // ── Laser sight ───────────────────────────────────────────────
  const laserLen = s * 1.4;
  const lf = 0.6 + Math.sin(t * 12) * 0.15 + Math.sin(t * 37) * 0.08;
  const segs = 8;
  for (let i = 0; i < segs; i++) {
    const f0 = i / segs;
    const f1 = (i + 1) / segs;
    const x0 = s * 1.06 + laserLen * f0;
    const x1 = s * 1.06 + laserLen * f1;
    const fa = (1 - f0) * (1 - f0);
    g.moveTo(x0, 0); g.lineTo(x1, 0);
    g.stroke({ color: 0x22ff44, width: 3, alpha: lf * 0.08 * fa });
    g.moveTo(x0, 0); g.lineTo(x1, 0);
    g.stroke({ color: 0x66ff88, width: 0.9, alpha: lf * 0.55 * fa });
  }

  // ── Turret dome ───────────────────────────────────────────────
  // Cast shadow (¾-view: shifted south)
  g.circle(s * 0.01, s * 0.06, s * 0.37);
  g.fill({ color: 0x000000, alpha: 0.3 });

  // Base sphere
  g.circle(0, 0, s * 0.36);
  g.fill(DOME_COLOR);
  g.stroke({ color: 0x50576a, width: 1.3 });

  // Rim shadow (darkens bottom/sides for depth)
  g.circle(0, 0, s * 0.36);
  g.fill({ color: 0x000000, alpha: 0.12 });

  // Large specular highlight (soft)
  g.circle(-s * 0.09, -s * 0.10, s * 0.18);
  g.fill({ color: 0xffffff, alpha: 0.12 });
  // Tight specular dot
  g.circle(-s * 0.10, -s * 0.12, s * 0.07);
  g.fill({ color: 0xffffff, alpha: 0.55 });
  // Tiny hot-spot
  g.circle(-s * 0.12, -s * 0.13, s * 0.025);
  g.fill({ color: 0xffffff, alpha: 0.9 });
  // ¾-view: south-facing crescent highlight (dome curvature seen from slightly above)
  g.ellipse(0, s * 0.22, s * 0.22, s * 0.08);
  g.fill({ color: 0xffffff, alpha: 0.06 });

  // ── Weapons ───────────────────────────────────────────────────
  if (weaponSlots && weaponSlots.length > 0) {
    // Use layout offsets for the slot count
    const offsets = SLOT_OFFSETS[Math.min(weaponSlots.length, 4) - 1]!;
    for (let i = 0; i < weaponSlots.length && i < offsets.length; i++) {
      // Skip side hardpoints — they're drawn on separate rotating containers
      if (sideSlotIndices && sideSlotIndices.includes(i)) continue;
      drawWeaponSlot(g, s, t, weaponSlots[i]!, offsets[i]!);
    }
  } else {
    // Fallback: draw a single default barrel (original design)
    drawDefaultBarrel(g, s);
  }

  // ── Headlights ───────────────────────────────────────────────
  const hf = 0.72 + Math.sin(t * 15) * 0.08 + Math.sin(t * 41) * 0.05;
  // Shift headlights outward when many slots to avoid overlap
  const hlY = (weaponSlots && weaponSlots.length >= 3) ? 0.32 : 0.26;
  for (const sy of [-hlY, hlY]) {
    // Recess
    g.circle(s * 0.72, s * sy, s * 0.085);
    g.fill({ color: 0x000000, alpha: 0.55 });
    // Outer glow
    g.circle(s * 0.72, s * sy, s * 0.18);
    g.fill({ color: 0xddeeff, alpha: hf * 0.10 });
    g.circle(s * 0.72, s * sy, s * 0.12);
    g.fill({ color: 0xeef6ff, alpha: hf * 0.28 });
    // Bright lens
    g.circle(s * 0.72, s * sy, s * 0.065);
    g.fill({ color: 0xffffff, alpha: hf * 0.98 });
    // Specular flare
    g.circle(s * 0.712, s * sy - s * 0.024, s * 0.02);
    g.fill({ color: 0xffffff, alpha: hf * 0.7 });
  }

  // ── Sensor eye ────────────────────────────────────────────────
  const ep = 0.7 + Math.sin(t * 3) * 0.3;
  // Iris
  g.circle(0, 0, s * 0.20);
  g.fill({ color: stateColor, alpha: ep * 0.18 });
  g.circle(0, 0, s * 0.135);
  g.fill({ color: stateColor, alpha: ep * 0.45 });
  // Pupil
  g.circle(0, 0, s * 0.07);
  g.fill({ color: 0xffffff, alpha: ep * 0.85 });
  // Eye specular
  g.circle(-s * 0.028, -s * 0.028, s * 0.022);
  g.fill({ color: 0xffffff, alpha: ep * 0.9 });

  // Sensor glow ring
  g.circle(0, 0, s * 0.32);
  g.stroke({ color: stateColor, width: 1.5, alpha: 0.2 + Math.sin(t * 2.5) * 0.15 });

  // ── Antenna ───────────────────────────────────────────────────
  // Shaft shadow
  g.moveTo(s * 0.008, -s * 0.36);
  g.lineTo(s * 0.008, -s * 0.54);
  g.stroke({ color: 0x000000, alpha: 0.4, width: 1.8 });
  // Shaft
  g.moveTo(0, -s * 0.36);
  g.lineTo(0, -s * 0.54);
  g.stroke({ color: 0x9098a8, width: 1.5 });
  // Shaft highlight
  g.moveTo(-s * 0.003, -s * 0.36);
  g.lineTo(-s * 0.003, -s * 0.54);
  g.stroke({ color: 0xd0d8e8, alpha: 0.5, width: 0.5 });

  const ab = Math.sin(t * 6) > 0.3 ? 0.9 : 0.05;
  g.circle(0, -s * 0.56, s * 0.14);
  g.fill({ color: 0x20ff60, alpha: ab * 0.12 });
  g.circle(0, -s * 0.56, s * 0.07);
  g.fill({ color: 0x20ff60, alpha: ab * 0.45 });
  g.circle(0, -s * 0.56, s * 0.035);
  g.fill({ color: 0x70ffaa, alpha: ab });
  // Antenna tip specular
  g.circle(-s * 0.014, -s * 0.574, s * 0.012);
  g.fill({ color: 0xffffff, alpha: ab * 0.8 });
}

/** Legacy single barrel — used when no weapon slot data is provided */
function drawDefaultBarrel(g: Graphics, s: number) {
  // Barrel mount/shoulder
  g.roundRect(s * 0.24, -s * 0.15, s * 0.15, s * 0.30, 2);
  g.fill(BARREL_MOUNT_COLOR);
  g.stroke({ color: 0x1a1e28, width: 0.8 });

  // Main barrel tube
  g.roundRect(s * 0.34, -s * 0.10, s * 0.62, s * 0.20, 2);
  g.fill(BARREL_COLOR);
  g.stroke({ color: 0x303848, width: 0.8 });

  // Barrel highlight
  g.roundRect(s * 0.35, -s * 0.097, s * 0.60, s * 0.030, 1);
  g.fill({ color: 0xffffff, alpha: 0.10 });

  // Barrel rings
  for (const bx of [0.44, 0.60, 0.76]) {
    g.rect(s * bx, -s * 0.103, s * 0.026, s * 0.206);
    g.fill(BARREL_RING_COLOR);
    g.stroke({ color: 0x1e2230, width: 0.5 });
    g.rect(s * bx, -s * 0.103, s * 0.026, s * 0.034);
    g.fill({ color: 0xffffff, alpha: 0.15 });
  }

  // Muzzle brake
  g.rect(s * 0.90, -s * 0.082, s * 0.16, s * 0.164);
  g.fill(MUZZLE_COLOR);
  g.stroke({ color: 0x202430, width: 0.6 });
  for (const my of [-0.038, 0.038]) {
    g.rect(s * 0.905, s * my - s * 0.012, s * 0.15, s * 0.024);
    g.fill(0x060810);
  }
  g.rect(s * 0.90, -s * 0.082, s * 0.16, s * 0.014);
  g.fill({ color: 0xffffff, alpha: 0.12 });
}
